import { useState, useEffect, useCallback } from "react";
import { useParams } from "react-router-dom";
import { Plus, Package, ArrowDownCircle, ArrowUpCircle, AlertTriangle, FileBarChart } from "lucide-react";
import { toast } from "sonner";
import { apiClient } from "../utils/apiClient";
import { getUser } from "../utils/authService";
import BackNavigation from "../components/BackNavigation";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

// Helper function to extract error message from API response
const getErrorMessage = (error, defaultMsg = "An error occurred") => {
  const detail = error.response?.data?.detail;
  if (!detail) return defaultMsg;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ');
  }
  if (typeof detail === 'object') {
    return detail.msg || detail.message || JSON.stringify(detail);
  }
  return defaultMsg;
};

// Extracted components for better maintainability
import { MATERIAL_CATEGORIES, applyRecordFilters, EMPTY_RECORD_FILTERS, MachineUsageTab, exportToXlsx, describeFilters } from "../components/materials";
import StockRegisterSection from "../components/materials/sections/StockRegisterSection";
import StockAlertsSection from "../components/materials/sections/StockAlertsSection";
import CategoryReportsSection from "../components/materials/sections/CategoryReportsSection";
import FuelTrackingSection from "../components/materials/sections/FuelTrackingSection";
import RequirementsSection from "../components/materials/sections/RequirementsSection";
import InwardSection from "../components/materials/sections/InwardSection";
import OutwardSection from "../components/materials/sections/OutwardSection";
import ApprovalsSection from "../components/materials/sections/ApprovalsSection";
import ApprovalDialogSection from "../components/materials/sections/ApprovalDialogSection";
import RequirementFormSection from "../components/materials/sections/RequirementFormSection";
import MaterialFormSection from "../components/materials/sections/MaterialFormSection";
import InwardFormSection from "../components/materials/sections/InwardFormSection";
import OutwardFormSection from "../components/materials/sections/OutwardFormSection";
import ExcelUploadDialogSection from "../components/materials/sections/ExcelUploadDialogSection";
import OpeningStockDialogSection from "../components/materials/sections/OpeningStockDialogSection";
import RateHistoryDialogSection from "../components/materials/sections/RateHistoryDialogSection";
import DocUploadDialogSection from "../components/materials/sections/DocUploadDialogSection";
import FuelHistoryDialogSection from "../components/materials/sections/FuelHistoryDialogSection";

const MaterialManagement = () => {
  const { projectId } = useParams();
  const [project, setProject] = useState(null);
  const [materials, setMaterials] = useState([]);
  const [stockSummary, setStockSummary] = useState([]);
  const [inwardRecords, setInwardRecords] = useState([]);
  const [outwardRecords, setOutwardRecords] = useState([]);
  const [requirements, setRequirements] = useState([]);
  const [pendingApprovals, setPendingApprovals] = useState([]);
  const [activeTab, setActiveTab] = useState("stock");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogType, setDialogType] = useState("material"); // material, inward, outward, requirement, opening_stock
  const [editingMaterial, setEditingMaterial] = useState(null);
  const [approvalDialogOpen, setApprovalDialogOpen] = useState(false);
  const [selectedInward, setSelectedInward] = useState(null);
  const [openingStockDialogOpen, setOpeningStockDialogOpen] = useState(false);
  
  // Bulk selection state
  const [selectedInwardIds, setSelectedInwardIds] = useState([]);
  const [selectedOutwardIds, setSelectedOutwardIds] = useState([]);
  const [inwardFilters, setInwardFilters] = useState(EMPTY_RECORD_FILTERS);
  const [outwardFilters, setOutwardFilters] = useState(EMPTY_RECORD_FILTERS);
  
  // Rate History state
  const [rateHistoryOpen, setRateHistoryOpen] = useState(false);
  const [rateHistoryData, setRateHistoryData] = useState({ material_name: "", current_rate: 0, history: [] });
  const [loadingRateHistory, setLoadingRateHistory] = useState(false);
  
  const currentUser = getUser() || {};
  const isMasterAdmin = currentUser.email === "shiva.mittal@kpminfra.com" || currentUser.role === "Master Admin";
  const canApprove = isMasterAdmin || ["Management", "Project Director", "Project Manager"].includes(currentUser.role);
  // Non-admin users can only do Inward and Outward - no Add Material, Opening Stock, Edit, Delete
  const canEditMaterials = isMasterAdmin;
  
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  
  const [openingStockForm, setOpeningStockForm] = useState({
    material_id: "",
    quantity: "",
    rate: "",
    remarks: ""
  });
  
  const [materialForm, setMaterialForm] = useState({
    name: "",
    category: "construction",
    unit: "MT",
    opening_stock: "",
    rate: "",
    minimum_stock: ""
  });

  const [inwardForm, setInwardForm] = useState({
    material_id: "",
    material_requirement_id: "",
    category: "construction",
    date: new Date().toISOString().split('T')[0],
    quantity: "",
    entered_rate: "",
    challan_number: "",
    supplier_name: "",
    vehicle_number: "",
    received_by: "",
    quality_status: "Approved",
    remarks: "",
    // Bill/Invoice tracking
    bill_received: false,
    bill_number: "",
    bill_date: ""
  });
  
  const [editingInward, setEditingInward] = useState(null);
  const [editingOutward, setEditingOutward] = useState(null);

  const [outwardForm, setOutwardForm] = useState({
    material_id: "",
    material_requirement_id: "",
    category: "construction",
    date: new Date().toISOString().split('T')[0],
    quantity: "",
    issued_to: "",
    purpose: "",
    remarks: "",
    // Fuel tracking fields
    is_fuel_issue: false,
    equipment_id: "",
    fuel_type: "",
    current_hour_reading: "",
    current_km_reading: "",
    // BOQ-linked outward fields
    is_boq_linked: false,
    boq_item_id: "",
    boq_quantity: ""
  });

  // Equipment list for fuel tracking
  const [equipmentList, setEquipmentList] = useState([]);
  const [equipmentSearchTerm, setEquipmentSearchTerm] = useState("");
  const [equipmentFuelHistory, setEquipmentFuelHistory] = useState(null);
  const [loadingFuelHistory, setLoadingFuelHistory] = useState(false);
  
  // State for detailed fuel history view
  const [selectedEquipmentForHistory, setSelectedEquipmentForHistory] = useState(null);
  const [detailedFuelHistory, setDetailedFuelHistory] = useState([]);
  const [loadingDetailedHistory, setLoadingDetailedHistory] = useState(false);
  const [fuelHistoryDialogOpen, setFuelHistoryDialogOpen] = useState(false);
  
  // BOQ Items for BOQ-linked outward
  const [boqItems, setBoqItems] = useState([]);
  const [selectedBoqRecipe, setSelectedBoqRecipe] = useState([]);
  const [boqLinkedSubmitting, setBoqLinkedSubmitting] = useState(false);

  const [requirementForm, setRequirementForm] = useState({
    material_id: "",
    planned_quantity: "",
    is_lumpsum: false,
    management_rate: "",
    rate_variance_allowed: "3",
    remarks: ""
  });
  
  // Edit requirement state
  const [editingRequirement, setEditingRequirement] = useState(null);

  const [approvalRemarks, setApprovalRemarks] = useState("");

  // Excel Upload State
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  
  // GRN Document Upload State
  const [docUploadDialogOpen, setDocUploadDialogOpen] = useState(false);
  const [selectedInwardForDocs, setSelectedInwardForDocs] = useState(null);
  const [docFile, setDocFile] = useState(null);
  const [docType, setDocType] = useState("Invoice");
  const [uploadingDoc, setUploadingDoc] = useState(false);

  const fetchProject = useCallback(async () => {
    try {
      const response = await apiClient.get(`/projects/${projectId}`);
      setProject(response.data);
    } catch (error) {
      console.error("Failed to fetch project");
    }
  }, [projectId]);

  const fetchMaterials = useCallback(async () => {
    try {
      const response = await apiClient.get(`/materials/${projectId}`);
      setMaterials(response.data);
    } catch (error) {
      console.error("Failed to fetch materials");
    }
  }, [projectId]);

  const fetchStockSummary = useCallback(async () => {
    try {
      const response = await apiClient.get(`/materials/stock/${projectId}`);
      setStockSummary(response.data);
    } catch (error) {
      console.error("Failed to fetch stock summary");
    }
  }, [projectId]);

  const fetchInwardRecords = useCallback(async () => {
    try {
      const response = await apiClient.get(`/materials/inward/${projectId}`);
      setInwardRecords(response.data);
    } catch (error) {
      console.error("Failed to fetch inward records");
    }
  }, [projectId]);

  const fetchOutwardRecords = useCallback(async () => {
    try {
      const response = await apiClient.get(`/materials/outward/${projectId}`);
      setOutwardRecords(response.data);
    } catch (error) {
      console.error("Failed to fetch outward records");
    }
  }, [projectId]);

  const fetchRequirements = useCallback(async () => {
    try {
      const response = await apiClient.get(`/material-requirements/${projectId}`);
      setRequirements(response.data);
    } catch (error) {
      console.error("Failed to fetch requirements");
    }
  }, [projectId]);

  const fetchPendingApprovals = useCallback(async () => {
    try {
      const response = await apiClient.get(`/materials/inward/pending-approvals?project_id=${projectId}`);
      setPendingApprovals(response.data);
    } catch (error) {
      console.error("Failed to fetch pending approvals");
    }
  }, [projectId]);

  const fetchEquipmentList = useCallback(async () => {
    try {
      const response = await apiClient.get(`/equipment/${projectId}`);
      setEquipmentList(response.data);
    } catch (error) {
      console.error("Failed to fetch equipment");
    }
  }, [projectId]);

  // Fetch BOQ items for BOQ-linked outward
  const fetchBoqItems = useCallback(async () => {
    try {
      const response = await apiClient.get(`/boq/${projectId}`);
      setBoqItems(response.data);
    } catch (error) {
      console.error("Failed to fetch BOQ items");
    }
  }, [projectId]);

  // Fetch recipe for a BOQ item
  const fetchBoqRecipe = async (boqItemId) => {
    if (!boqItemId) {
      setSelectedBoqRecipe([]);
      return;
    }
    try {
      const response = await apiClient.get(`/boq/${boqItemId}/recipe`);
      setSelectedBoqRecipe(response.data.material_recipe || []);
    } catch (error) {
      console.error("Failed to fetch BOQ recipe");
      setSelectedBoqRecipe([]);
    }
  };

  // Handle BOQ-linked outward submission
  const handleBoqLinkedOutward = async () => {
    if (!outwardForm.boq_item_id || !outwardForm.boq_quantity || parseFloat(outwardForm.boq_quantity) <= 0) {
      toast.error("Please select a BOQ item and enter quantity");
      return;
    }
    
    setBoqLinkedSubmitting(true);
    try {
      const params = new URLSearchParams({
        project_id: projectId,
        boq_item_id: outwardForm.boq_item_id,
        boq_quantity: outwardForm.boq_quantity,
        outward_date: outwardForm.date,
        issued_to: outwardForm.issued_to || "Site Work",
        purpose: outwardForm.purpose || "BOQ Work Execution",
        remarks: outwardForm.remarks || ""
      });
      
      const response = await apiClient.post(`/materials/outward/boq-linked?${params.toString()}`);
      
      if (response.data.stock_warnings && response.data.stock_warnings.length > 0) {
        toast.warning(
          `Created ${response.data.outwards_created.length} outward records. ${response.data.stock_warnings.length} materials had insufficient stock.`,
          { duration: 5000 }
        );
      } else {
        toast.success(`Created ${response.data.outwards_created.length} material outward records for BOQ ${response.data.boq_item_code}`);
      }
      
      closeDialog();
      fetchMaterials();
      fetchStockSummary();
      fetchOutwardRecords();
      fetchBoqItems(); // Refresh BOQ to show updated completed quantity
    } catch (error) {
      toast.error(getErrorMessage(error, "Failed to create BOQ-linked outward"));
    } finally {
      setBoqLinkedSubmitting(false);
    }
  };

  // Fetch fuel history for selected equipment
  const fetchEquipmentFuelHistory = async (equipmentId, checkDate = null) => {
    if (!equipmentId) {
      setEquipmentFuelHistory(null);
      return;
    }
    setLoadingFuelHistory(true);
    try {
      const response = await apiClient.get(`/equipment/${equipmentId}/fuel-history?limit=5`);
      const historyData = response.data;
      
      // Also check for duplicate on the selected date
      const dateToCheck = checkDate || outwardForm.date;
      if (dateToCheck) {
        try {
          const duplicateCheck = await apiClient.get(`/equipment/${equipmentId}/fuel-check?check_date=${dateToCheck}`);
          historyData.duplicate_warning = duplicateCheck.data.has_existing ? duplicateCheck.data : null;
        } catch (e) {
          historyData.duplicate_warning = null;
        }
      }
      
      setEquipmentFuelHistory(historyData);
    } catch (error) {
      console.error("Failed to fetch fuel history:", error);
      setEquipmentFuelHistory(null);
    } finally {
      setLoadingFuelHistory(false);
    }
  };

  // Open detailed fuel history for a machine
  const openDetailedFuelHistory = async (equipment) => {
    setSelectedEquipmentForHistory(equipment);
    setFuelHistoryDialogOpen(true);
    setLoadingDetailedHistory(true);
    
    try {
      // Fetch complete fuel history from outward records
      const eqFuelIssues = outwardRecords
        .filter(o => o.is_fuel_issue && o.equipment_id === equipment.id)
        .sort((a, b) => new Date(b.date) - new Date(a.date)); // Most recent first
      
      setDetailedFuelHistory(eqFuelIssues);
    } catch (error) {
      console.error("Failed to load fuel history:", error);
      toast.error("Failed to load fuel history");
    } finally {
      setLoadingDetailedHistory(false);
    }
  };

  useEffect(() => {
    fetchProject();
    fetchMaterials();
    fetchStockSummary();
    fetchInwardRecords();
    fetchOutwardRecords();
    fetchRequirements();
    fetchPendingApprovals();
    fetchEquipmentList();
    fetchBoqItems();
  }, [fetchProject, fetchMaterials, fetchStockSummary, fetchInwardRecords, fetchOutwardRecords, fetchRequirements, fetchPendingApprovals, fetchEquipmentList, fetchBoqItems]);

  const handleApproveInward = async (inwardId, approveRate = true, approveQuantity = true, remarks = "") => {
    try {
      await apiClient.post(`/materials/inward/${inwardId}/approve?approve_rate=${approveRate}&approve_quantity=${approveQuantity}&remarks=${encodeURIComponent(remarks)}`);
      toast.success("Material inward approved");
      setApprovalDialogOpen(false);
      setSelectedInward(null);
      fetchInwardRecords();
      fetchPendingApprovals();
      fetchStockSummary();
    } catch (error) {
      toast.error(getErrorMessage(error, "Failed to approve"));
    }
  };

  const handleRejectInward = async (inwardId, remarks = "") => {
    try {
      await apiClient.post(`/materials/inward/${inwardId}/reject?remarks=${encodeURIComponent(remarks)}`);
      toast.success("Material inward rejected");
      setApprovalDialogOpen(false);
      setSelectedInward(null);
      fetchInwardRecords();
      fetchPendingApprovals();
    } catch (error) {
      toast.error(getErrorMessage(error, "Failed to reject"));
    }
  };

  const syncRequirements = async () => {
    try {
      await apiClient.post(`/material-requirements/sync/${projectId}`);
      toast.success("Requirements synced with actual consumption");
      fetchRequirements();
    } catch (error) {
      toast.error("Failed to sync requirements");
    }
  };

  const handleOpeningStockSubmit = async (e) => {
    e.preventDefault();
    try {
      const response = await apiClient.post(
        `/materials/opening-stock?project_id=${projectId}&material_id=${openingStockForm.material_id}&quantity=${openingStockForm.quantity}&rate=${openingStockForm.rate || 0}&remarks=${encodeURIComponent(openingStockForm.remarks || '')}`
      );
      toast.success(response.data.message);
      setOpeningStockDialogOpen(false);
      setOpeningStockForm({ material_id: "", quantity: "", rate: "", remarks: "" });
      fetchMaterials();
      fetchStockSummary();
    } catch (error) {
      toast.error(getErrorMessage(error, "Failed to add opening stock"));
    }
  };

  // Excel Upload Functions
  const handleExcelUpload = async (e) => {
    e.preventDefault();
    if (!uploadFile) {
      toast.error("Please select a file");
      return;
    }

    setUploading(true);
    setUploadResult(null);

    try {
      const formData = new FormData();
      formData.append('file', uploadFile);

      const response = await apiClient.post(
        `/material-requirements/upload/${projectId}`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );

      setUploadResult(response.data);
      toast.success(response.data.message);
      fetchRequirements();
      fetchMaterials();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || "Failed to upload file";
      toast.error(errorMsg);
      setUploadResult({ error: errorMsg });
    } finally {
      setUploading(false);
    }
  };

  const downloadTemplate = () => {
    // Create sample Excel data
    const templateData = [
      ["material_name", "unit", "planned_quantity", "rate", "remarks"],
      ["Cement OPC 53 Grade", "MT", 500, 350, "For foundation work"],
      ["Steel TMT Bars", "MT", 200, 55000, "Fe500 grade"],
      ["Sand (River)", "Cum", 1000, 1800, "Fine aggregate"],
      ["Aggregate 20mm", "Cum", 800, 1500, "Coarse aggregate"],
      ["Diesel", "Ltr", 5000, 90, "For machinery"]
    ];

    // Convert to CSV
    const csv = templateData.map(row => row.join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "material_requirements_template.csv";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
    toast.success("Template downloaded");
  };

  const handleMaterialSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...materialForm,
        opening_stock: parseFloat(materialForm.opening_stock),
        rate: parseFloat(materialForm.rate),
        minimum_stock: parseFloat(materialForm.minimum_stock || 0)
      };

      if (editingMaterial) {
        await apiClient.put(`/materials/${editingMaterial.id}`, data);
        toast.success("Material updated successfully");
      } else {
        await apiClient.post('/materials', { ...data, project_id: projectId });
        toast.success("Material added successfully");
      }
      
      closeDialog();
      fetchMaterials();
      fetchStockSummary();
    } catch (error) {
      toast.error(`Failed to ${editingMaterial ? 'update' : 'add'} material`);
    }
  };

  const handleInwardSubmit = async (e) => {
    e.preventDefault();
    try {
      const { is_new_material, new_material_name, new_material_unit, ...form } = inwardForm;
      let materialId = form.material_id;
      if (is_new_material && form.category !== "construction" && !editingInward) {
        const name = (new_material_name || "").trim();
        const existing = materials.find(m => m.name.trim().toLowerCase() === name.toLowerCase() && (m.category || "construction") === form.category);
        if (existing) {
          materialId = existing.id;
        } else {
          const created = await apiClient.post('/materials', {
            project_id: projectId,
            name,
            category: form.category,
            unit: new_material_unit || "Nos",
            opening_stock: 0,
            rate: parseFloat(form.entered_rate) || 0,
            minimum_stock: 0
          });
          materialId = created.data.id;
          toast.success(`"${name}" added to Stock Register`);
        }
      }
      if (!materialId) {
        toast.error("Please select a material");
        return;
      }
      const data = {
        ...form,
        material_id: materialId,
        project_id: projectId,
        quantity: parseFloat(inwardForm.quantity),
        entered_rate: parseFloat(inwardForm.entered_rate) || 0,
        bill_received: inwardForm.bill_received || false,
        bill_number: inwardForm.bill_number || "",
        bill_date: inwardForm.bill_date || ""
      };
      
      if (editingInward) {
        await apiClient.put(`/materials/inward/${editingInward.id}`, data);
        toast.success("Inward entry updated");
      } else {
        const response = await apiClient.post('/materials/inward', data);
        if (response.data.requires_approval) {
          toast.warning(`Material inward recorded but requires approval: ${response.data.approval_reasons?.join(", ")}`);
        } else {
          toast.success("Material inward recorded and approved");
        }
      }
      
      closeDialog();
      setEditingInward(null);
      fetchMaterials();
      fetchStockSummary();
      fetchInwardRecords();
      fetchRequirements();
      fetchPendingApprovals();
    } catch (error) {
      toast.error(getErrorMessage(error, "Failed to record inward"));
    }
  };
  
  const handleEditInward = (record) => {
    setEditingInward(record);
    setInwardForm({
      material_id: record.material_id,
      material_requirement_id: record.material_requirement_id || "",
      category: record.category || "construction",
      date: record.date,
      quantity: record.quantity?.toString() || "",
      entered_rate: record.entered_rate?.toString() || "",
      challan_number: record.challan_number || "",
      supplier_name: record.supplier_name || "",
      vehicle_number: record.vehicle_number || "",
      received_by: record.received_by || "",
      quality_status: record.quality_status || "Approved",
      remarks: record.remarks || "",
      bill_received: record.bill_received || false,
      bill_number: record.bill_number || "",
      bill_date: record.bill_date || ""
    });
    setDialogType("inward");
    setDialogOpen(true);
  };

  const handleOutwardSubmit = async (e) => {
    e.preventDefault();
    
    // Validate quantity
    const quantity = parseFloat(outwardForm.quantity);
    if (isNaN(quantity) || quantity <= 0) {
      toast.error("Please enter a valid quantity");
      return;
    }
    
    try {
      // Build data object without spreading empty strings for numeric fields
      const data = {
        project_id: projectId,
        material_id: outwardForm.material_id,
        material_requirement_id: outwardForm.material_requirement_id || "",
        category: outwardForm.category || "construction",
        date: outwardForm.date,
        quantity: quantity,
        issued_to: outwardForm.issued_to,
        purpose: outwardForm.purpose,
        remarks: outwardForm.remarks || "",
        // Fuel tracking data
        is_fuel_issue: outwardForm.is_fuel_issue || false,
        equipment_id: outwardForm.equipment_id || "",
        fuel_type: outwardForm.is_fuel_issue ? outwardForm.fuel_type : "",
        current_hour_reading: outwardForm.is_fuel_issue && outwardForm.current_hour_reading ? parseFloat(outwardForm.current_hour_reading) : 0,
        current_km_reading: outwardForm.is_fuel_issue && outwardForm.current_km_reading ? parseFloat(outwardForm.current_km_reading) : 0,
        // BOQ-linked data (only include if actually linked)
        linked_to_boq: outwardForm.is_boq_linked || false,
        boq_item_id: outwardForm.is_boq_linked ? outwardForm.boq_item_id : "",
        boq_quantity: outwardForm.is_boq_linked && outwardForm.boq_quantity ? parseFloat(outwardForm.boq_quantity) : 0,
        auto_calculated: false,
        dpr_id: ""
      };
      
      if (editingOutward) {
        await apiClient.put(`/materials/outward/${editingOutward.id}`, data);
        toast.success("Outward entry updated");
      } else {
        const response = await apiClient.post('/materials/outward', data);
        
        // Show warning toast if duplicate detected
        if (response.data.warning) {
          toast.warning(response.data.warning, { duration: 6000 });
        }
        
        toast.success(outwardForm.is_fuel_issue ? "Fuel issue recorded to equipment" : "Material outward recorded");
      }
      
      closeDialog();
      setEditingOutward(null);
      fetchMaterials();
      fetchStockSummary();
      fetchOutwardRecords();
    } catch (error) {
      toast.error(getErrorMessage(error, "Failed to record outward"));
    }
  };
  
  const handleEditOutward = (record) => {
    setEditingOutward(record);
    setOutwardForm({
      material_id: record.material_id,
      material_requirement_id: record.material_requirement_id || "",
      category: record.category || "construction",
      date: record.date,
      quantity: record.quantity?.toString() || "",
      issued_to: record.issued_to || "",
      purpose: record.purpose || "",
      remarks: record.remarks || "",
      is_fuel_issue: record.is_fuel_issue || false,
      equipment_id: record.equipment_id || "",
      fuel_type: record.fuel_type || "",
      current_hour_reading: record.current_hour_reading?.toString() || "",
      current_km_reading: record.current_km_reading?.toString() || ""
    });
    setDialogType("outward");
    setDialogOpen(true);
  };

  const handleEdit = (material) => {
    setEditingMaterial(material);
    setMaterialForm({
      name: material.name,
      unit: material.unit,
      opening_stock: material.opening_stock?.toString() || "",
      rate: material.rate?.toString() || "",
      minimum_stock: material.minimum_stock?.toString() || ""
    });
    setDialogType("material");
    setDialogOpen(true);
  };

  const handleDelete = async (materialId, materialName) => {
    if (!window.confirm(`Are you sure you want to delete material "${materialName}"?`)) {
      return;
    }
    try {
      await apiClient.delete(`/materials/${materialId}`);
      toast.success("Material deleted successfully");
      fetchMaterials();
      fetchStockSummary();
    } catch (error) {
      toast.error("Failed to delete material");
    }
  };

  // Fetch rate history for a material
  const fetchRateHistory = async (materialId) => {
    setLoadingRateHistory(true);
    try {
      const response = await apiClient.get(`/materials/${materialId}/rate-history`);
      setRateHistoryData(response.data);
      setRateHistoryOpen(true);
    } catch (error) {
      toast.error("Failed to fetch rate history");
    } finally {
      setLoadingRateHistory(false);
    }
  };

  // Delete inward entry (Master Admin only)
  const handleDeleteInward = async (inwardId, grnNumber) => {
    if (!window.confirm(`Are you sure you want to delete inward entry "${grnNumber}"? This will reverse the stock quantity.`)) {
      return;
    }
    try {
      await apiClient.delete(`/materials/inward/${inwardId}`);
      toast.success("Inward entry deleted and stock adjusted");
      fetchMaterials();
      fetchStockSummary();
      fetchInwardRecords();
    } catch (error) {
      toast.error(getErrorMessage(error, "Failed to delete inward entry"));
    }
  };

  // Delete outward entry (Master Admin only)
  const handleDeleteOutward = async (outwardId, issueNumber) => {
    if (!window.confirm(`Are you sure you want to delete outward entry "${issueNumber}"? This will reverse the stock quantity.`)) {
      return;
    }
    try {
      await apiClient.delete(`/materials/outward/${outwardId}`);
      toast.success("Outward entry deleted and stock adjusted");
      fetchMaterials();
      fetchStockSummary();
      fetchOutwardRecords();
    } catch (error) {
      toast.error(getErrorMessage(error, "Failed to delete outward entry"));
    }
  };

  // Bulk delete inward entries (Master Admin only)
  const handleBulkDeleteInward = async () => {
    if (selectedInwardIds.length === 0) {
      toast.error("No entries selected");
      return;
    }
    if (!window.confirm(`Are you sure you want to delete ${selectedInwardIds.length} inward entries? This will reverse all stock quantities.`)) {
      return;
    }
    try {
      await apiClient.post('/materials/inward/bulk-delete', { ids: selectedInwardIds });
      toast.success(`${selectedInwardIds.length} inward entries deleted and stock adjusted`);
      setSelectedInwardIds([]);
      fetchMaterials();
      fetchStockSummary();
      fetchInwardRecords();
    } catch (error) {
      toast.error(getErrorMessage(error, "Failed to delete inward entries"));
    }
  };

  // Bulk delete outward entries (Master Admin only)
  const handleBulkDeleteOutward = async () => {
    if (selectedOutwardIds.length === 0) {
      toast.error("No entries selected");
      return;
    }
    if (!window.confirm(`Are you sure you want to delete ${selectedOutwardIds.length} outward entries? This will reverse all stock quantities.`)) {
      return;
    }
    try {
      await apiClient.post('/materials/outward/bulk-delete', { ids: selectedOutwardIds });
      toast.success(`${selectedOutwardIds.length} outward entries deleted and stock adjusted`);
      setSelectedOutwardIds([]);
      fetchMaterials();
      fetchStockSummary();
      fetchOutwardRecords();
    } catch (error) {
      toast.error(getErrorMessage(error, "Failed to delete outward entries"));
    }
  };

  // Toggle select all inward
  const toggleSelectAllInward = () => {
    if (selectedInwardIds.length === filteredInward.length) {
      setSelectedInwardIds([]);
    } else {
      setSelectedInwardIds(filteredInward.map(r => r.id));
    }
  };

  // Toggle select all outward
  const toggleSelectAllOutward = () => {
    if (selectedOutwardIds.length === filteredOutward.length) {
      setSelectedOutwardIds([]);
    } else {
      setSelectedOutwardIds(filteredOutward.map(r => r.id));
    }
  };
  
  // GRN Document Upload Functions
  const openDocUploadDialog = (inwardRecord) => {
    setSelectedInwardForDocs(inwardRecord);
    setDocFile(null);
    setDocType("Invoice");
    setDocUploadDialogOpen(true);
  };
  
  const handleDocUpload = async () => {
    if (!docFile || !selectedInwardForDocs) return;
    
    setUploadingDoc(true);
    try {
      const formData = new FormData();
      formData.append('file', docFile);
      formData.append('doc_type', docType);
      
      await apiClient.post(
        `/materials/inward/${selectedInwardForDocs.id}/documents`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      
      toast.success("Document uploaded successfully");
      setDocUploadDialogOpen(false);
      setDocFile(null);
      fetchInwardRecords();
    } catch (error) {
      toast.error(getErrorMessage(error, "Failed to upload document"));
    } finally {
      setUploadingDoc(false);
    }
  };
  
  const handleDeleteDocument = async (inwardId, docId) => {
    if (!window.confirm("Delete this document?")) return;
    
    try {
      await apiClient.delete(`/materials/inward/${inwardId}/documents/${docId}`);
      toast.success("Document deleted");
      fetchInwardRecords();
    } catch (error) {
      toast.error("Failed to delete document");
    }
  };

  // View document with authentication
  const handleViewDocument = async (doc) => {
    try {
      // doc.url starts with /api/, but apiClient already has /api as baseURL
      // So we need to remove the /api prefix to avoid /api/api/
      const urlPath = doc.url.replace(/^\/api/, '');
      const response = await apiClient.get(urlPath, {
        responseType: 'blob'
      });
      
      // Create blob URL and open in new tab
      const blob = new Blob([response.data], { type: response.headers['content-type'] || 'application/pdf' });
      const blobUrl = window.URL.createObjectURL(blob);
      window.open(blobUrl, '_blank');
      
      // Clean up blob URL after a delay
      setTimeout(() => window.URL.revokeObjectURL(blobUrl), 60000);
    } catch (error) {
      console.error("Document view error:", error);
      toast.error("Failed to open document");
    }
  };

  const openDialog = (type) => {
    setDialogType(type);
    setEditingMaterial(null);
    if (type === "material") {
      setMaterialForm({ name: "", unit: "MT", opening_stock: "", rate: "", minimum_stock: "" });
    } else if (type === "inward") {
      // Only show materials that have requirements
      const materialsWithRequirements = materials.filter(m => 
        requirements.some(r => r.material_id === m.id)
      );
      const firstMaterial = materialsWithRequirements[0];
      const firstRequirement = firstMaterial ? requirements.find(r => r.material_id === firstMaterial.id) : null;
      
      setInwardForm({
        material_id: firstMaterial?.id || "",
        material_requirement_id: firstRequirement?.id || "",
        date: new Date().toISOString().split('T')[0],
        quantity: "",
        entered_rate: firstMaterial?.rate || "",  // Pre-fill with Stock Register rate
        challan_number: "",
        supplier_name: "",
        vehicle_number: "",
        received_by: "",
        quality_status: "Approved",
        remarks: ""
      });
    } else if (type === "outward") {
      setOutwardForm({
        material_id: "",
        material_requirement_id: "",
        category: "construction",  // Set default category
        date: new Date().toISOString().split('T')[0],
        quantity: "",
        issued_to: "",
        purpose: "",
        remarks: "",
        is_fuel_issue: false,
        equipment_id: "",
        fuel_type: "",
        current_hour_reading: "",
        current_km_reading: "",
        // BOQ-linked fields
        is_boq_linked: false,
        boq_item_id: "",
        boq_quantity: ""
      });
      setSelectedBoqRecipe([]);
    } else if (type === "requirement") {
      // Filter materials that don't have requirements yet
      const existingIds = requirements.map(r => r.material_id);
      const available = materials.filter(m => !existingIds.includes(m.id));
      setRequirementForm({
        material_id: available[0]?.id || "",
        planned_quantity: "",
        is_lumpsum: false,
        management_rate: "",
        rate_variance_allowed: "3",
        remarks: ""
      });
    }
    setDialogOpen(true);
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setEditingMaterial(null);
    setEditingInward(null);
    setEditingOutward(null);
    setEditingRequirement(null);
    setEquipmentSearchTerm("");
    setEquipmentFuelHistory(null);
  };

  const handleRequirementSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...requirementForm,
        project_id: projectId,
        planned_quantity: parseFloat(requirementForm.planned_quantity),
        is_lumpsum: requirementForm.is_lumpsum,
        management_rate: parseFloat(requirementForm.management_rate) || 0,
        rate_variance_allowed: parseFloat(requirementForm.rate_variance_allowed) || 3.0
      };
      
      if (editingRequirement) {
        // Update existing requirement
        await apiClient.put(`/material-requirements/${editingRequirement.id}`, data);
        toast.success("Material requirement updated");
      } else {
        // Create new requirement
        await apiClient.post('/material-requirements', data);
        toast.success("Material requirement added");
      }
      closeDialog();
      fetchRequirements();
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to ${editingRequirement ? 'update' : 'add'} requirement`);
    }
  };

  // Edit requirement handler
  const handleEditRequirement = (req) => {
    setEditingRequirement(req);
    setRequirementForm({
      material_id: req.material_id,
      planned_quantity: req.planned_quantity?.toString() || "",
      is_lumpsum: req.is_lumpsum || false,
      management_rate: req.management_rate?.toString() || "",
      rate_variance_allowed: req.rate_variance_allowed?.toString() || "3",
      remarks: req.remarks || ""
    });
    setDialogType("requirement");
    setDialogOpen(true);
  };

  const handleDeleteRequirement = async (reqId) => {
    if (!window.confirm("Delete this material requirement?")) return;
    try {
      await apiClient.delete(`/material-requirements/${reqId}`);
      toast.success("Requirement deleted");
      fetchRequirements();
    } catch (error) {
      toast.error("Failed to delete requirement");
    }
  };

  // Export stock report to Excel
  const exportToExcel = () => {
    // Create CSV content
    let csvContent = "data:text/csv;charset=utf-8,";
    
    // Add report header
    csvContent += `KPM Infrastructure - Stock Report\n`;
    csvContent += `Project: ${project?.name || 'N/A'}\n`;
    csvContent += `Generated: ${new Date().toLocaleString()}\n\n`;
    
    // Add summary
    csvContent += `SUMMARY\n`;
    csvContent += `Total Materials,${materials.length}\n`;
    csvContent += `Total Inventory Value,₹${totalInventoryValue.toLocaleString('en-IN')}\n`;
    csvContent += `Low Stock Items,${lowStockCount}\n\n`;
    
    // Add category-wise breakdown
    MATERIAL_CATEGORIES.forEach((cat) => {
      const categoryItems = stockSummary.filter(s => s.category === cat.id || (!s.category && cat.id === "construction"));
      if (categoryItems.length === 0) return;
      
      const totalValue = categoryItems.reduce((sum, s) => sum + (s.stock_value || 0), 0);
      
      csvContent += `\n${cat.label.toUpperCase()}\n`;
      csvContent += `Material,Unit,Opening Stock,Total Inward,Total Outward,Current Stock,Min Stock,Rate,Stock Value,Status\n`;
      
      categoryItems.forEach((item) => {
        const status = item.low_stock_alert ? "Low Stock" : "OK";
        csvContent += `"${item.name}",${item.unit},${item.opening_stock?.toFixed(2)},${item.total_inward?.toFixed(2)},${item.total_outward?.toFixed(2)},${item.current_stock?.toFixed(2)},${item.minimum_stock?.toFixed(2)},${item.rate?.toFixed(2)},${item.stock_value?.toFixed(2)},${status}\n`;
      });
      
      csvContent += `Category Total,,,,,,,,₹${totalValue.toFixed(2)},\n`;
    });
    
    // Add grand total
    csvContent += `\nGRAND TOTAL,,,,,,,,₹${totalInventoryValue.toFixed(2)},\n`;
    
    // Create download link
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", `Stock_Report_${project?.name?.replace(/[^a-z0-9]/gi, '_') || 'Project'}_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    toast.success("Stock report exported to Excel (CSV format)");
  };

  const getMaterialName = (materialId) => {
    const mat = materials.find(m => m.id === materialId);
    return mat ? mat.name : "Unknown";
  };

  const summarize = (rows, filters, rateOf) => {
    const unit = filters.material_id !== "all" ? materials.find(m => m.id === filters.material_id)?.unit || "" : "";
    return {
      count: rows.length,
      qty: filters.material_id !== "all" ? rows.reduce((s, r) => s + (r.quantity || 0), 0) : null,
      unit,
      value: rows.reduce((s, r) => s + (r.quantity || 0) * rateOf(r), 0)
    };
  };
  const materialRate = (r) => materials.find(m => m.id === r.material_id)?.rate || 0;
  const filteredInward = applyRecordFilters(inwardRecords, inwardFilters, { materials, partyKey: "supplier_name", getMaterialName });
  const filteredOutward = applyRecordFilters(outwardRecords, outwardFilters, { materials, partyKey: "issued_to", getMaterialName });
  const inwardSummary = summarize(filteredInward, inwardFilters, (r) => r.entered_rate || materialRate(r));
  const outwardSummary = summarize(filteredOutward, outwardFilters, materialRate);
  const getEquipmentLabel = (id) => {
    const eq = equipmentList.find(e => e.id === id);
    return eq ? `${eq.name}${eq.equipment_id ? ` (${eq.equipment_id})` : ""}` : "";
  };
  const pastSuppliers = [...new Set(inwardRecords.map(r => (r.supplier_name || "").trim()).filter(Boolean))].sort();
  const catLabel = (r) => (MATERIAL_CATEGORIES.find(c => c.id === (r.category || materials.find(m => m.id === r.material_id)?.category)) || MATERIAL_CATEGORIES[0]).label;
  const matUnit = (r) => materials.find(m => m.id === r.material_id)?.unit || "";
  const numCol = (header, key, total) => ({ header, key, width: 13, numFmt: "#,##0.00", total });

  const exportInward = async () => {
    await exportToXlsx({
      fileName: `Material_Inward_${inwardFilters.from || "all"}_${inwardFilters.to || "all"}.xlsx`,
      title: `Material Inward (GRN) Register${project?.name ? ` - ${project.name}` : ""}`,
      subtitle: describeFilters(inwardFilters, { materials, partyLabel: "Supplier" }),
      sheetName: "Inward",
      columns: [
        { header: "GRN No.", key: "grn", width: 14 }, { header: "Date", key: "date", width: 12 }, { header: "Category", key: "cat", width: 20 },
        { header: "Material", key: "mat", width: 26 }, { header: "Unit", key: "unit", width: 8 }, numCol("Qty", "qty"), numCol("Rate (₹)", "rate"),
        numCol("Value (₹)", "value", true), { header: "Supplier", key: "supplier", width: 22 }, { header: "Challan", key: "challan", width: 14 },
        { header: "Vehicle", key: "vehicle", width: 14 }, { header: "Bill", key: "bill", width: 14 }, { header: "Status", key: "status", width: 12 }, { header: "Remarks", key: "remarks", width: 24 }
      ],
      rows: filteredInward.map(r => {
        const rate = r.entered_rate || materialRate(r);
        return { grn: r.grn_number, date: r.date, cat: catLabel(r), mat: getMaterialName(r.material_id), unit: matUnit(r), qty: r.quantity || 0, rate, value: (r.quantity || 0) * rate,
          supplier: r.supplier_name, challan: r.challan_number, vehicle: r.vehicle_number, bill: r.bill_received ? (r.bill_number || "Received") : "Pending", status: r.status || r.quality_status, remarks: r.remarks };
      })
    });
    toast.success("Inward entries exported to Excel");
  };

  const exportOutward = async () => {
    await exportToXlsx({
      fileName: `Material_Outward_${outwardFilters.from || "all"}_${outwardFilters.to || "all"}.xlsx`,
      title: `Material Outward (Issue) Register${project?.name ? ` - ${project.name}` : ""}`,
      subtitle: describeFilters(outwardFilters, { materials, partyLabel: "Issued To" }),
      sheetName: "Outward",
      columns: [
        { header: "Issue No.", key: "issue", width: 14 }, { header: "Date", key: "date", width: 12 }, { header: "Category", key: "cat", width: 20 },
        { header: "Material", key: "mat", width: 26 }, { header: "Unit", key: "unit", width: 8 }, numCol("Qty", "qty"), numCol("Rate (₹)", "rate"),
        numCol("Value (₹)", "value", true), { header: "Issued To", key: "to", width: 20 }, { header: "Machine", key: "machine", width: 24 },
        { header: "Purpose", key: "purpose", width: 22 }, { header: "Remarks", key: "remarks", width: 24 }
      ],
      rows: filteredOutward.map(r => {
        const rate = materialRate(r);
        return { issue: r.issue_number, date: r.date, cat: catLabel(r), mat: getMaterialName(r.material_id), unit: matUnit(r), qty: r.quantity || 0, rate, value: (r.quantity || 0) * rate,
          to: r.issued_to, machine: getEquipmentLabel(r.equipment_id), purpose: r.purpose, remarks: r.remarks };
      })
    });
    toast.success("Outward entries exported to Excel");
  };

  const totalInventoryValue = stockSummary.reduce((sum, m) => sum + (m.stock_value || 0), 0);
  const lowStockCount = stockSummary.filter(m => m.low_stock_alert).length;

  const ctx = {
    approvalDialogOpen,
    approvalRemarks,
    boqItems,
    boqLinkedSubmitting,
    canApprove,
    closeDialog,
    detailedFuelHistory,
    docFile,
    docType,
    docUploadDialogOpen,
    downloadTemplate,
    editingInward,
    editingMaterial,
    editingOutward,
    editingRequirement,
    equipmentFuelHistory,
    equipmentList,
    equipmentSearchTerm,
    exportInward,
    exportOutward,
    exportToExcel,
    fetchBoqRecipe,
    fetchEquipmentFuelHistory,
    fetchRateHistory,
    filteredInward,
    filteredOutward,
    fuelHistoryDialogOpen,
    getEquipmentLabel,
    getMaterialName,
    handleApproveInward,
    handleBoqLinkedOutward,
    handleBulkDeleteInward,
    handleBulkDeleteOutward,
    handleDelete,
    handleDeleteDocument,
    handleDeleteInward,
    handleDeleteOutward,
    handleDeleteRequirement,
    handleDocUpload,
    handleEdit,
    handleEditInward,
    handleEditOutward,
    handleEditRequirement,
    handleExcelUpload,
    handleInwardSubmit,
    handleMaterialSubmit,
    handleOpeningStockSubmit,
    handleOutwardSubmit,
    handleRejectInward,
    handleRequirementSubmit,
    handleViewDocument,
    inwardFilters,
    inwardForm,
    inwardRecords,
    inwardSummary,
    isMasterAdmin,
    loadingDetailedHistory,
    loadingFuelHistory,
    loadingRateHistory,
    lowStockCount,
    materialForm,
    materials,
    openDetailedFuelHistory,
    openDialog,
    openDocUploadDialog,
    openingStockDialogOpen,
    openingStockForm,
    outwardFilters,
    outwardForm,
    outwardRecords,
    outwardSummary,
    pastSuppliers,
    pendingApprovals,
    project,
    rateHistoryData,
    rateHistoryOpen,
    requirementForm,
    requirements,
    searchTerm,
    selectedBoqRecipe,
    selectedCategory,
    selectedEquipmentForHistory,
    selectedInward,
    selectedInwardForDocs,
    selectedInwardIds,
    selectedOutwardIds,
    setActiveTab,
    setApprovalDialogOpen,
    setApprovalRemarks,
    setDocFile,
    setDocType,
    setDocUploadDialogOpen,
    setEquipmentSearchTerm,
    setFuelHistoryDialogOpen,
    setInwardFilters,
    setInwardForm,
    setMaterialForm,
    setOpeningStockDialogOpen,
    setOpeningStockForm,
    setOutwardFilters,
    setOutwardForm,
    setRateHistoryOpen,
    setRequirementForm,
    setSearchTerm,
    setSelectedBoqRecipe,
    setSelectedCategory,
    setSelectedInward,
    setSelectedInwardIds,
    setSelectedOutwardIds,
    setUploadDialogOpen,
    setUploadFile,
    setUploadResult,
    stockSummary,
    syncRequirements,
    toggleSelectAllInward,
    toggleSelectAllOutward,
    totalInventoryValue,
    uploadDialogOpen,
    uploadFile,
    uploadResult,
    uploading,
    uploadingDoc
  };

  return (
    <div>
      <BackNavigation title="Material Management" projectName={project?.name} />
      
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="font-heading font-bold text-4xl text-primary tracking-tight">MATERIAL MANAGEMENT</h1>
          <p className="text-slate-600 mt-2 font-body">Inventory tracking with inward/outward</p>
        </div>
        <div className="flex space-x-2">
          <button onClick={() => openDialog("inward")} data-testid="add-inward-button" className="btn-outline flex items-center space-x-2 border-success text-success hover:bg-success/10">
            <ArrowDownCircle className="w-4 h-4" />
            <span>Inward</span>
          </button>
          <button onClick={() => openDialog("outward")} data-testid="add-outward-button" className="btn-outline flex items-center space-x-2 border-warning text-warning hover:bg-warning/10">
            <ArrowUpCircle className="w-4 h-4" />
            <span>Outward</span>
          </button>
          {canEditMaterials && (
          <button 
            onClick={() => {
              setOpeningStockForm({ material_id: materials[0]?.id || "", quantity: "", rate: "", remarks: "" });
              setOpeningStockDialogOpen(true);
            }} 
            className="btn-outline flex items-center space-x-2 border-primary text-primary hover:bg-primary/10"
          >
            <Package className="w-4 h-4" />
            <span>Opening Stock</span>
          </button>
          )}
          {canEditMaterials && (
          <button onClick={() => openDialog("material")} data-testid="add-material-button" className="btn-primary flex items-center space-x-2">
            <Plus className="w-4 h-4" />
            <span>Add Material</span>
          </button>
          )}
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid md:grid-cols-4 gap-4 mb-8">
        <div className="stat-card">
          <p className="text-xs uppercase tracking-widest text-slate-600 font-medium mb-2">Total Inventory Value</p>
          <p className="font-heading font-black text-3xl text-primary font-mono" data-testid="inventory-value">₹{totalInventoryValue.toLocaleString('en-IN')}</p>
        </div>
        <div className="stat-card">
          <p className="text-xs uppercase tracking-widest text-slate-600 font-medium mb-2">Total Materials</p>
          <p className="font-heading font-black text-3xl text-accent font-mono" data-testid="total-materials">{materials.length}</p>
        </div>
        <div className="stat-card cursor-pointer hover:bg-error/5 transition-colors" onClick={() => lowStockCount > 0 && setActiveTab("alerts")}>
          <p className="text-xs uppercase tracking-widest text-slate-600 font-medium mb-2">Low Stock Alerts</p>
          <p className={`font-heading font-black text-3xl font-mono ${lowStockCount > 0 ? 'text-error' : 'text-success'}`} data-testid="low-stock-count">
            {lowStockCount > 0 && <AlertTriangle className="w-6 h-6 inline mr-2" />}
            {lowStockCount}
          </p>
        </div>
        <div className="stat-card cursor-pointer hover:bg-primary/5 transition-colors" onClick={() => setActiveTab("reports")}>
          <p className="text-xs uppercase tracking-widest text-slate-600 font-medium mb-2">Category Reports</p>
          <p className="font-heading font-black text-3xl text-primary font-mono">
            <FileBarChart className="w-6 h-6 inline mr-2" />
            {MATERIAL_CATEGORIES.length}
          </p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex flex-wrap gap-1 mb-6 bg-slate-100 p-1 rounded-sm w-fit">
        {["stock", "alerts", "reports", "fuel", "requirements", "inward", "outward", "machine_usage", "approvals"].map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`px-4 py-2 text-sm font-medium rounded-sm transition-colors relative ${
              activeTab === tab ? 'bg-white text-primary shadow-sm' : 'text-slate-600 hover:text-primary'
            }`}
            data-testid={`tab-${tab}`}
          >
            {tab === "stock" ? "Stock Register" : 
             tab === "alerts" ? "Stock Alerts" :
             tab === "reports" ? "Category Reports" :
             tab === "fuel" ? "⛽ Fuel Tracking" :
             tab === "inward" ? "Inward (GRN)" : 
             tab === "outward" ? "Outward (Issue)" : 
             tab === "machine_usage" ? "Machine Usage" :
             tab === "requirements" ? "Requirements" :
             "Pending Approvals"}
            {tab === "alerts" && lowStockCount > 0 && (
              <span className="absolute -top-1 -right-1 bg-error text-white text-xs w-5 h-5 rounded-full flex items-center justify-center">
                {lowStockCount}
              </span>
            )}
            {tab === "approvals" && pendingApprovals.length > 0 && (
              <span className="absolute -top-1 -right-1 bg-error text-white text-xs w-5 h-5 rounded-full flex items-center justify-center">
                {pendingApprovals.length}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Stock Register Tab */}
      {activeTab === "stock" && <StockRegisterSection ctx={ctx} />}

      {/* Stock Alerts Tab */}
      {activeTab === "alerts" && <StockAlertsSection ctx={ctx} />}

      {/* Category Reports Tab */}
      {activeTab === "reports" && <CategoryReportsSection ctx={ctx} />}

      {/* Fuel Tracking Tab */}
      {activeTab === "fuel" && <FuelTrackingSection ctx={ctx} />}

      {/* Requirements Tab */}
      {activeTab === "requirements" && <RequirementsSection ctx={ctx} />}

      {/* Inward Records Tab */}
      {activeTab === "inward" && <InwardSection ctx={ctx} />}

      {/* Outward Records Tab */}
      {activeTab === "outward" && <OutwardSection ctx={ctx} />}

      {activeTab === "machine_usage" && (
        <MachineUsageTab outwardRecords={outwardRecords} materials={materials} equipmentList={equipmentList} projectName={project?.name} />
      )}

      {/* Pending Approvals Tab */}
      {activeTab === "approvals" && <ApprovalsSection ctx={ctx} />}

      {/* Approval Dialog */}
      <ApprovalDialogSection ctx={ctx} />

      {/* Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl">
              {dialogType === "material" ? (editingMaterial ? 'EDIT MATERIAL' : 'ADD MATERIAL') : 
               dialogType === "inward" ? (editingInward ? 'EDIT INWARD (GRN)' : 'MATERIAL INWARD (GRN)') : 
               dialogType === "outward" ? (editingOutward ? 'EDIT OUTWARD (ISSUE)' : 'MATERIAL OUTWARD (ISSUE)') : 
               dialogType === "requirement" ? (editingRequirement ? 'EDIT MATERIAL REQUIREMENT' : 'ADD MATERIAL REQUIREMENT') : 'ADD MATERIAL REQUIREMENT'}
            </DialogTitle>
          </DialogHeader>

          {/* Requirement Form */}
          {dialogType === "requirement" && <RequirementFormSection ctx={ctx} />}

          {/* Material Form */}
          {dialogType === "material" && <MaterialFormSection ctx={ctx} />}

          {/* Inward Form */}
          {dialogType === "inward" && <InwardFormSection ctx={ctx} />}

          {/* Outward Form */}
          {dialogType === "outward" && <OutwardFormSection ctx={ctx} />}
        </DialogContent>
      </Dialog>

      {/* Excel Upload Dialog */}
      <ExcelUploadDialogSection ctx={ctx} />

      {/* Opening Stock Dialog */}
      <OpeningStockDialogSection ctx={ctx} />

      {/* Rate History Dialog */}
      <RateHistoryDialogSection ctx={ctx} />
      
      {/* Document Upload Dialog */}
      <DocUploadDialogSection ctx={ctx} />

      {/* Fuel History Dialog */}
      <FuelHistoryDialogSection ctx={ctx} />
    </div>
  );
};

export default MaterialManagement;
